import type { Node as PmNode } from '@tiptap/pm/model';
import { Plugin, PluginKey, type EditorState, type Transaction } from '@tiptap/pm/state';
import { Decoration, DecorationSet, type EditorView } from '@tiptap/pm/view';

/** A misspelled word in the document, with its suggestions, in document positions. */
export interface SpellRange {
  from: number;
  to: number;
  word: string;
  suggestions: string[];
}

export interface SpellClick {
  range: SpellRange;
  /** Viewport coordinates of the word, for the suggestions popup. */
  left: number;
  top: number;
  bottom: number;
}

interface SpellPluginState {
  ranges: SpellRange[];
  decorations: DecorationSet;
}

export const spellKey = new PluginKey<SpellPluginState>('airpost-spell');
export const SPELL_CLICK_EVENT = 'airpost:spell-click';

/** The document's text as the checker should see it, with the position of every text node. */
export function collectText(doc: PmNode): { text: string; segments: Array<{ from: number; start: number; length: number }> } {
  const parts: string[] = [];
  const segments: Array<{ from: number; start: number; length: number }> = [];
  let start = 0;
  doc.descendants((node, pos) => {
    if (node.isText && node.text) {
      parts.push(node.text);
      segments.push({ from: pos, start, length: node.text.length });
      start += node.text.length + 1; // joined with '\n'
    }
    return true;
  });
  return { text: parts.join('\n'), segments };
}

/** Map the checker's offsets back to document positions. */
export function toRanges(
  misspelled: Array<{ word: string; offset: number; length: number; suggestions: string[] }>,
  segments: Array<{ from: number; start: number; length: number }>,
  ignored: ReadonlySet<string>,
): SpellRange[] {
  const out: SpellRange[] = [];
  let i = 0;
  for (const m of misspelled) {
    if (ignored.has(m.word)) continue;
    while (i < segments.length && segments[i]!.start + segments[i]!.length <= m.offset) i++;
    const seg = segments[i];
    if (!seg || m.offset < seg.start || m.offset + m.length > seg.start + seg.length) continue; // spans nodes: skip
    const from = seg.from + (m.offset - seg.start);
    out.push({ from, to: from + m.length, word: m.word, suggestions: m.suggestions });
  }
  return out;
}

function build(doc: PmNode, ranges: SpellRange[]): DecorationSet {
  return DecorationSet.create(
    doc,
    ranges.map((r) => Decoration.inline(r.from, r.to, { class: 'misspelled', title: r.suggestions.length ? r.suggestions.join(', ') : undefined } as Record<string, string>)),
  );
}

/** Red wavy underlines under misspelled words; a click on one raises SPELL_CLICK_EVENT on the window. */
export function spellPlugin(): Plugin<SpellPluginState> {
  return new Plugin<SpellPluginState>({
    key: spellKey,
    state: {
      init: () => ({ ranges: [], decorations: DecorationSet.empty }),
      apply(tr: Transaction, prev: SpellPluginState): SpellPluginState {
        const meta = tr.getMeta(spellKey) as { ranges: SpellRange[] } | undefined;
        if (meta) return { ranges: meta.ranges, decorations: build(tr.doc, meta.ranges) };
        if (!tr.docChanged) return prev;
        // keep the underlines in place while the journalist types elsewhere; the next check replaces them
        const ranges = prev.ranges
          .map((r) => ({ ...r, from: tr.mapping.map(r.from), to: tr.mapping.map(r.to) }))
          .filter((r) => r.to > r.from && tr.doc.textBetween(r.from, r.to) === r.word);
        return { ranges, decorations: build(tr.doc, ranges) };
      },
    },
    props: {
      decorations: (state: EditorState) => spellKey.getState(state)?.decorations ?? null,
      handleClick(view: EditorView, pos: number): boolean {
        const st = spellKey.getState(view.state);
        const range = st?.ranges.find((r) => pos >= r.from && pos <= r.to);
        if (!range) return false;
        const a = view.coordsAtPos(range.from);
        const b = view.coordsAtPos(range.to);
        window.dispatchEvent(new CustomEvent<SpellClick>(SPELL_CLICK_EVENT, { detail: { range, left: a.left, top: a.top, bottom: Math.max(a.bottom, b.bottom) } }));
        return true;
      },
    },
  });
}

export function setSpellRanges(view: EditorView, ranges: SpellRange[]): void {
  view.dispatch(view.state.tr.setMeta(spellKey, { ranges }));
}

export function currentRanges(state: EditorState): SpellRange[] {
  return spellKey.getState(state)?.ranges ?? [];
}
